import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createAlbum } from "../../services/galleryService";
import { getAllPackages } from "../../services/paymentService";
import Button from "./Button";
import Input from "./Input";
import Modal from "./Modal";
import Switch from "./Switch";
import { color } from "../../styles/theme";
import { toast } from "react-toastify";
import ARoomAlbumPolicyFields, { AROOM_ALBUM_VISIBILITY } from "./ARoomAlbumPolicyFields";

const CreateAlbumModal = ({ galleryId, galleryName, isARoom = false, isPrivateARoom = false, onClose, onCreated }) => {
  const { t } = useTranslation("common");
  const [form, setForm] = useState({
    name: "",
    IsPremium: false,
    PackageId: "",
    MakeGalleryPremium: false,
    IsWatermark: false,
    isFundraising: false,
    visibilityDelayHours: "",
    NotifyPromoterOnLive: false,
    NotifyUsersOnLive: false,
    ContainsMinors: false,
    ARoomVisibility: isPrivateARoom ? AROOM_ALBUM_VISIBILITY.VerifiedMembers : AROOM_ALBUM_VISIBILITY.Public,
    ConsentConfirmed: false,
  });
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packagesLoaded, setPackagesLoaded] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!form.IsPremium || packagesLoaded || packagesLoading) return;

    const loadPackages = async () => {
      setPackagesLoading(true);
      try {
        const res = await getAllPackages();
        const raw = Array.isArray(res.data?.Result)
          ? res.data.Result
          : Array.isArray(res.data)
            ? res.data
            : [];
        setPackages(raw.filter((pkg) => pkg?.IsActive !== false));
        setPackagesLoaded(true);
      } catch (err) {
        toast.error(err.response?.data?.Message || t("createAlbumModal.loadPackagesError"));
      } finally {
        setPackagesLoading(false);
      }
    };

    loadPackages();
  }, [form.IsPremium, packagesLoaded, packagesLoading, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.warning(t("createAlbumModal.nameRequired")); return; }
    if (form.IsPremium && !form.PackageId) {
      toast.warning(t("createAlbumModal.packageRequired"));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        EventGalleryId: Number(galleryId),
        Name: form.name.trim(),
        IsPremium: form.IsPremium,
        PackageId: form.IsPremium ? Number(form.PackageId) : null,
        MakeGalleryPremium: form.MakeGalleryPremium,
        VisibilityDelayHours: form.visibilityDelayHours !== "" ? Number(form.visibilityDelayHours) : null,
        IsFundRaising: form.IsPremium ? form.isFundraising : false,
        IsWatermark: form.IsWatermark,
        NotifyPromoterOnLive: form.NotifyPromoterOnLive,
        NotifyUsersOnLive: form.NotifyUsersOnLive,
        ...(isARoom ? {
          ContainsMinors: form.ContainsMinors,
          ARoomVisibility: form.ContainsMinors ? AROOM_ALBUM_VISIBILITY.IndividuallyApproved : form.ARoomVisibility,
          ConsentConfirmed: form.ConsentConfirmed,
        } : {}),
      };

      const res = await createAlbum(payload);
      toast.success(res.data?.Message || t("createAlbumModal.createSuccess"));
      onCreated(res.data?.Result);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("createAlbumModal.createError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t("createAlbumModal.title")}
      subtitle={t("createAlbumModal.subtitle", { galleryName })}
      onClose={onClose}
      busy={loading}
      maxWidth={460}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">{t("actions.cancel")}</Button>
          <Button onClick={handleSubmit} disabled={loading}>{loading ? t("createAlbumModal.creating") : t("createAlbumModal.create")}</Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <Input label={t("createAlbumModal.nameLabel")} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("createAlbumModal.namePlaceholder")} required />

        <div style={s.switchGroup}>
          <SwitchRow label={t("createAlbumModal.watermarkLabel")} checked={form.IsWatermark} onChange={(v) => set("IsWatermark", v)} />
          <SwitchRow
            label={t("createAlbumModal.premiumLabel")}
            checked={form.IsPremium}
            onChange={(v) => { set("IsPremium", v); if (!v) { set("PackageId", ""); set("isFundraising", false); } }}
            last={!form.IsPremium}
          />
          {form.IsPremium && (
            <div style={s.selectWrap}>
              <label style={s.selectLabel}>{t("createAlbumModal.packagePriceLabel")}</label>
              <select value={form.PackageId} onChange={(e) => set("PackageId", e.target.value)} required={form.IsPremium} className="input">
                <option value="">{t("createAlbumModal.packagePricePlaceholder")}</option>
                {packages.map((pkg) => (
                  <option key={pkg.Id} value={pkg.Id}>
                    {Number(pkg.DefaultPrice || 0).toFixed(2)} {pkg.Currency}
                  </option>
                ))}
              </select>
              {packagesLoading && <div style={s.selectHint}>{t("createAlbumModal.loadingPackagePrices")}</div>}
            </div>
          )}
          {form.IsPremium && (
            <SwitchRow label={t("createAlbumModal.donationsLabel")} checked={form.isFundraising} onChange={(v) => set("isFundraising", v)} last />
          )}
        </div>
        {isARoom && <ARoomAlbumPolicyFields value={form} onChange={setForm} isPrivateARoom={isPrivateARoom} />}
      </form>
    </Modal>
  );
};

const SwitchRow = ({ label, checked, onChange, last = false }) => (
  <div style={{ ...s.switchRow, ...(last ? { borderBottom: "none" } : {}) }}>
    <span style={s.switchLabel}>{label}</span>
    <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />
  </div>
);

const s = {
  switchGroup: { display: "flex", flexDirection: "column", marginBottom: 4 },
  switchRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 2px", borderBottom: `1px solid ${color.gray100}` },
  switchLabel: { fontSize: 13.5, color: color.gray800, fontWeight: 500 },
  selectWrap: { padding: "10px 2px 2px" },
  selectLabel: { display: "block", marginBottom: 6, fontWeight: 600, fontSize: 13, color: color.gray700 },
  selectHint: { marginTop: 6, fontSize: 12, color: color.textMuted },
};

export default CreateAlbumModal;
